import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import QRCodeStyling from 'qr-code-styling'
import type { QRSettings } from '../../types/qr'
import './QRPreview.scss'

const DOWNLOAD_SIZE = 1024

interface QRPreviewProps {
  settings: QRSettings
}

export interface QRPreviewRef {
  downloadPNG: (filename: string) => void
  downloadSVG: (filename: string) => void
  getBlob: () => Promise<Blob | null>
  hasLogo: () => boolean
}

const createHighResQR = (settings: QRSettings) => {
  return new QRCodeStyling({
    width: DOWNLOAD_SIZE,
    height: DOWNLOAD_SIZE,
    data: settings.value || ' ',
    margin: Math.round(settings.margin * (DOWNLOAD_SIZE / settings.size)),
    qrOptions: {
      errorCorrectionLevel: settings.ecLevel,
    },
    dotsOptions: {
      color: settings.fgColor,
      type: settings.dotType,
    },
    cornersSquareOptions: {
      color: settings.fgColor,
      type: settings.cornerSquareType,
    },
    cornersDotOptions: {
      color: settings.fgColor,
      type: settings.cornerDotType,
    },
    backgroundOptions: {
      color: settings.bgColor,
    },
    imageOptions: {
      crossOrigin: 'anonymous',
      margin: Math.round(5 * (DOWNLOAD_SIZE / settings.size)),
      imageSize: settings.logoSize / 100,
    },
    image: settings.logoImage,
  })
}

export const QRPreview = forwardRef<QRPreviewRef, QRPreviewProps>(
  ({ settings }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const qrCodeRef = useRef<QRCodeStyling | null>(null)

    useImperativeHandle(ref, () => ({
      downloadPNG: (filename: string) => {
        const highResQR = createHighResQR(settings)
        highResQR.download({ name: filename, extension: 'png' })
      },
      downloadSVG: (filename: string) => {
        const highResQR = createHighResQR(settings)
        highResQR.download({ name: filename, extension: 'svg' })
      },
      getBlob: async () => {
        const highResQR = createHighResQR(settings)
        return await highResQR.getRawData('png') as Blob | null
      },
      hasLogo: () => !!settings.logoImage,
    }))

    useEffect(() => {
      if (!containerRef.current) return

      const qrCode = new QRCodeStyling({
        width: settings.size,
        height: settings.size,
        data: settings.value || ' ',
        margin: settings.margin,
        qrOptions: {
          errorCorrectionLevel: settings.ecLevel,
        },
        dotsOptions: {
          color: settings.fgColor,
          type: settings.dotType,
        },
        cornersSquareOptions: {
          color: settings.fgColor,
          type: settings.cornerSquareType,
        },
        cornersDotOptions: {
          color: settings.fgColor,
          type: settings.cornerDotType,
        },
        backgroundOptions: {
          color: settings.bgColor,
        },
        imageOptions: {
          crossOrigin: 'anonymous',
          margin: 5,
          imageSize: settings.logoSize / 100,
        },
        image: settings.logoImage,
      })

      containerRef.current.innerHTML = ''
      qrCode.append(containerRef.current)
      qrCodeRef.current = qrCode

      return () => {
        if (containerRef.current) {
          containerRef.current.innerHTML = ''
        }
      }
    }, [])

    useEffect(() => {
      if (!qrCodeRef.current) return

      qrCodeRef.current.update({
        width: settings.size,
        height: settings.size,
        data: settings.value || ' ',
        margin: settings.margin,
        qrOptions: {
          errorCorrectionLevel: settings.ecLevel,
        },
        dotsOptions: {
          color: settings.fgColor,
          type: settings.dotType,
        },
        cornersSquareOptions: {
          color: settings.fgColor,
          type: settings.cornerSquareType,
        },
        cornersDotOptions: {
          color: settings.fgColor,
          type: settings.cornerDotType,
        },
        backgroundOptions: {
          color: settings.bgColor,
        },
        imageOptions: {
          crossOrigin: 'anonymous',
          margin: 5,
          imageSize: settings.logoSize / 100,
        },
        image: settings.logoImage,
      })
    }, [settings])

    const containerStyle = {
      borderWidth: `${settings.borderWidth}px`,
      borderStyle: settings.borderWidth > 0 ? 'solid' : 'none',
      borderColor: settings.borderColor,
      borderRadius: `${settings.borderRadius}px`,
      overflow: 'hidden' as const,
    }

    return (
      <div className="qr-preview">
        <div className="qr-container" style={containerStyle}>
          <div ref={containerRef} className="qr-canvas" />
        </div>
        {!settings.value && (
          <p className="placeholder-text">Enter a URL to generate QR code</p>
        )}
      </div>
    )
  }
)

QRPreview.displayName = 'QRPreview'
